import React, { useEffect, useRef, useState } from "react";
import { ReactComponent as BedroomIcon } from "assets/icons/bedroom.svg";
import { ReactComponent as BathIcon } from "assets/icons/bath.svg";
import { ReactComponent as AreaIcon } from "assets/icons/area.svg";
import { ReactComponent as BookmarkIcon } from "assets/icons/bookmark.svg";
import { ReactComponent as QuestionIcon } from "assets/icons/question.svg";
import { ReactComponent as RemoveIcon } from "assets/icons/remove.svg";
import { ReactComponent as RoomIcon } from "assets/icons/locals_filter.svg";
import PropertyCardImageSlider from "./PropertyCardImageSlider";
import { ReactComponent as LocationIcon } from "assets/icons/location.svg";
import { formatCurrency } from "utils/formatNumber";
import TooltipCursor from "components/Tooltip/TooltipCursor";
import RemoveWarningDialog from "components/Dialog/RemoveWarningDialog";
import useBookmark from "hooks/useBookmark";
import { formatPropertyLocation } from "utils/formatText";
import useDialog from "hooks/useDialog";
import useAuth from "hooks/useAuth";
import ChooseAndCreateOrganiseList from "components/Dialog/ChooseAndCreateOrganiseList";
import useProperties from "hooks/useProperties";
import PropertyNoteDialog from "components/Dialog/PropertyNoteDialog";
import { useNavigate } from "react-router";

const PropertyCard = ({
  isAddedBy,
  property,
  setSearchCardHeight,
  isOrganisePage,
  isDiscardedProperty,
  setIsDiscardedProperty,
}) => {
  const [isTruncated, setIsTruncated] = useState(false);
  const textRef = useRef(null);
  const locationTextRef = useRef(null);
  const [locationTextHeight, setLocationTextHeight] = useState(0);
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [isBookmarkPropertyHasNote, setIsBookmarkPropertyHasNote] =
    useState(null);
  const [isDoubtProperty, setIsDoubtProperty] = useState(false);
  const {
    bookmarkProperty,
    removeSavedBookmark,
    bookmarkedPropertyIds,
    updateBookmarkNote,
    loading,
  } = useBookmark();

  const { openSigninDialog } = useDialog();
  const { user } = useAuth();
  const [removeWarningPopupOpen, setRemoveWarningPopupOpen] = useState(null);
  const [actionType, setActionType] = useState(null);
  const [conflictingActionType, setConflictingActionType] = useState(null);
  const {
    doubtPropertyIds,
    discardedPropertyIds,
    addDoubtProperty,
    removeDoubtProperty,
    addDiscardedProperty,
    removeDiscardedProperty,
    toggleLocationItelligencePopup,
    handleMapToggle,
  } = useProperties();
  const [chooseOrganiseList, setChooseOrganiseList] = useState(false);
  const navigation = useNavigate();

  useEffect(() => {
    // Check if the current property is bookmarked, doubted, or discarded
    const isPropertyBookmarked =
      bookmarkedPropertyIds.length > 0 &&
      bookmarkedPropertyIds?.some(
        (bookmark) => bookmark.propertyId === property._id
      );

    const isBookmarkPropertyHasNote =
      (bookmarkedPropertyIds.length > 0 &&
        bookmarkedPropertyIds.find(
          (bookmark) =>
            bookmark.propertyId === property._id &&
            bookmark.note &&
            bookmark.note.trim() !== ""
        )?.note) ||
      null;

    const isPropertyDoubted =
      doubtPropertyIds.length > 0 &&
      doubtPropertyIds?.some((id) => id === property._id);

    const isPropertyDiscarded =
      discardedPropertyIds.length > 0 &&
      discardedPropertyIds?.some((id) => id === property._id);

    // Update state based on the checks
    setIsBookmarked(isPropertyBookmarked);
    setIsBookmarkPropertyHasNote(isBookmarkPropertyHasNote);
    setIsDoubtProperty(isPropertyDoubted);
    setIsDiscardedProperty(isPropertyDiscarded);
  }, [
    bookmarkedPropertyIds,
    doubtPropertyIds,
    discardedPropertyIds,
    property._id,
  ]);

  useEffect(() => {
    const checkTruncation = () => {
      if (textRef.current) {
        const { scrollHeight, clientHeight } = textRef.current;
        setIsTruncated(scrollHeight > clientHeight);
      }
    };

    checkTruncation();
    window.addEventListener("resize", checkTruncation);
    return () => window.removeEventListener("resize", checkTruncation);
  }, [property?.title]);

  // Check if the location text has wrapped
  useEffect(() => {
    const observer = new ResizeObserver((entries) => {
      for (let entry of entries) {
        const { height } = entry.contentRect;

        if (height > locationTextHeight) {
          setSearchCardHeight(493);
        }
      }
    });

    if (locationTextRef.current) {
      setLocationTextHeight(locationTextRef.current.offsetHeight);
      observer.observe(locationTextRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, [setSearchCardHeight]);

  // Handle add  bookmark
  const handleAddBookmark = async () => {
    setIsBookmarked(true);
    try {
      await bookmarkProperty(property?._id, property?.average_gross_yield);
    } catch (error) {
      setIsBookmarked(false);
    }
  };

  const getRemoveMessage = (type) => {
    switch (type) {
      case "bookmark":
        return "Vuoi rimuovere questa proprietà dall’organizer?";
      case "doubt":
        return "Vuoi rimuovere questa proprietà dalla lista dubbi?";
      case "discard":
        return "Vuoi rimuovere questa proprietà dalla lista delle proprietà scartate?";
      default:
        return "";
    }
  };

  // Handle bookmark, doubt and remove property
  const handleAction = async (type) => {
    if (!user) {
      openSigninDialog();
      return;
    }

    setActionType(type);
    const conflictMessages = {
      bookmark: {
        doubt:
          "Per aggiungere la proprietà nell'organizer bisogna rimuoverla dalla lista dubbi, vuoi rimuoverla?",
        discard:
          "Per aggiungere la proprietà nell'organizer bisogna rimuoverla dalla lista delle proprietà scartate, vuoi rimuoverla?",
      },
      doubt: {
        bookmark:
          "Per aggiungere la proprietà nella lista dubbi bisogna rimuoverla dall'organizer, vuoi rimuoverla?",
        discard:
          "Per aggiungere la proprietà nella lista dubbi bisogna rimuoverla dalla lista delle proprietà scartate, vuoi rimuoverla?",
      },
      discard: {
        bookmark:
          "Se vuoi scartare la proprietà devi rimuoverla dalla lista dubbi, vuoi rimuoverla?",
        doubt:
          "Se vuoi scartare la proprietà bisogna rimuoverla dalla lista dubbi, vuoi rimuoverla?",
      },
    };

    if (type === "bookmark") {
      if (isBookmarked) {
        setRemoveWarningPopupOpen(getRemoveMessage("bookmark"));
        setConflictingActionType(null);
      } else if (isDoubtProperty) {
        setRemoveWarningPopupOpen(conflictMessages.bookmark.doubt);
        setConflictingActionType("doubt");
      } else if (isDiscardedProperty) {
        setRemoveWarningPopupOpen(conflictMessages.bookmark.discard);
        setConflictingActionType("discard");
      } else {
        setChooseOrganiseList(true);
      }
    } else if (type === "doubt") {
      if (isDoubtProperty) {
        setRemoveWarningPopupOpen(getRemoveMessage("doubt"));
        setConflictingActionType(null);
      } else if (isBookmarked) {
        setRemoveWarningPopupOpen(conflictMessages.doubt.bookmark);
        setConflictingActionType("bookmark");
      } else if (isDiscardedProperty) {
        setRemoveWarningPopupOpen(conflictMessages.doubt.discard);
        setConflictingActionType("discard");
      } else {
        setIsDoubtProperty(true);
        await addDoubtProperty(property._id, property?.average_gross_yield);
      }
    } else if (type === "discard") {
      if (isDiscardedProperty) {
        setRemoveWarningPopupOpen(getRemoveMessage("discard"));
        setConflictingActionType(null);
      } else if (isBookmarked) {
        setRemoveWarningPopupOpen(conflictMessages.discard.bookmark);
        setConflictingActionType("bookmark");
      } else if (isDoubtProperty) {
        setRemoveWarningPopupOpen(conflictMessages.discard.doubt);
        setConflictingActionType("doubt");
      } else {
        setIsDiscardedProperty(true);
        await addDiscardedProperty(property._id, property?.average_gross_yield);
      }
    }
  };

  // Handle remove and add property when click warning popup confirm button
  const handleRemoveConfirm = async () => {
    try {
      if (actionType === "bookmark") {
        if (conflictingActionType === "doubt") {
          // remove doubt
          setIsDoubtProperty(false);
          await removeDoubtProperty(property._id);
          // add bookmark
          setChooseOrganiseList(true);
        } else if (conflictingActionType === "discard") {
          // remove discard
          setIsDiscardedProperty(false);
          await removeDiscardedProperty(property._id);
          // add bookmark
          setChooseOrganiseList(true);
        } else {
          setIsBookmarked(false);
          await removeSavedBookmark(property._id);
        }
      } else if (actionType === "doubt") {
        if (conflictingActionType === "bookmark") {
          // remove bookmark
          setIsBookmarked(false);
          await removeSavedBookmark(property._id);

          // add doubt
          setIsDoubtProperty(true);
          await addDoubtProperty(property._id, property?.average_gross_yield);
        } else if (conflictingActionType === "discard") {
          // remove discard
          setIsDiscardedProperty(false);
          await removeDiscardedProperty(property._id);

          // add doubt
          setIsDoubtProperty(true);
          await addDoubtProperty(property._id, property?.average_gross_yield);
        } else {
          setIsDoubtProperty(false);
          await removeDoubtProperty(property._id);
        }
      } else if (actionType === "discard") {
        if (conflictingActionType === "bookmark") {
          // remove bookmark
          setIsBookmarked(false);
          await removeSavedBookmark(property._id);

          // add discard
          setIsDiscardedProperty(true);
          await addDiscardedProperty(
            property._id,
            property?.average_gross_yield
          );
        } else if (conflictingActionType === "doubt") {
          // remove doubt
          setIsDoubtProperty(false);
          await removeDoubtProperty(property._id);

          // add discard
          setIsDiscardedProperty(true);
          await addDiscardedProperty(
            property._id,
            property?.average_gross_yield
          );
        } else {
          setIsDiscardedProperty(false);
          await removeDiscardedProperty(property._id);
        }
      }
    } catch (error) {
      // revert orginal form if error
      if (actionType === "bookmark") {
        if (conflictingActionType === "doubt") {
          setIsDoubtProperty(true);
          setIsBookmarked(false);
        } else if (conflictingActionType === "discard") {
          setIsDiscardedProperty(true);
          setIsBookmarked(false);
        } else {
          setIsBookmarked(true);
        }
      } else if (actionType === "doubt") {
        if (conflictingActionType === "bookmark") {
          setIsBookmarked(true);
          setIsDoubtProperty(false);
        } else if (conflictingActionType === "discard") {
          setIsDiscardedProperty(true);
          setIsDoubtProperty(false);
        } else {
          setIsDoubtProperty(true);
        }
      } else if (actionType === "discard") {
        if (conflictingActionType === "bookmark") {
          setIsBookmarked(true);
          setIsDiscardedProperty(false);
        } else if (conflictingActionType === "doubt") {
          setIsDoubtProperty(true);
          setIsDiscardedProperty(false);
        } else {
          setIsDiscardedProperty(true);
        }
      }
      console.error(`Error removing ${actionType}:`, error);
    } finally {
      setRemoveWarningPopupOpen(null);
      setActionType(null);
      setConflictingActionType(null);
    }
  };

  // Update bookmark note
  const handleUpdateNote = async (note) => {
    try {
      await updateBookmarkNote(property?._id, note);
      setIsBookmarkPropertyHasNote(true);
    } catch (error) {}
  };

  return (
    <>
      <div className="w-full min-h-[414px] xl:w-[33.31%] h-full border-[1px] border-[#F0EFFB] rounded-[8px] flex flex-col">
        <div className="w-full">
          <div className="grid grid-cols-1">
            <PropertyCardImageSlider
              isBookmarked={isBookmarked}
              property={property}
              isOrganisePage={isOrganisePage}
              isBookmarkPropertyHasNote={isBookmarkPropertyHasNote}
            />
          </div>
          <div className="pl-4 mt-[16px] lg:mt-[19px]  pr-3 lg:px-[29.88px] bg-white">
            <div className="flex justify-between relative">
              <div className="">
                <h2 className="text-primary font-bold lg:text-[36px] text-[24px] -tracking-[1px] leading-[29px] lg:leading-[36px]">
                  {formatCurrency(property.sale_price, property?.sale_currency)}
                </h2>
              </div>
              <div className="rotate-90 lg:rotate-0 absolute -right-[33px] lg:-right-[7px] -top-[42px] h-[64px] lg:h-[85px] w-[130px] lg:w-[184px] z-[1] bg-white rounded-[11px] border-[1px] border-[#F0F0F0] flex items-center pl-[10px] lg:pl-[17px] pr-[10px] lg:pr-[22.83px] py-[8px] lg:py-[18px]">
                <BookmarkIcon
                  onClick={() => handleAction("bookmark")}
                  className={`${
                    isBookmarked // Differnet bookmarked color for organise page
                      ? isOrganisePage
                        ? "text-[#000000]"
                        : "text-[#7BCD11]"
                      : "hover:text-[#7BCD1133] text-white"
                  } cursor-pointer  stroke-black -rotate-90  lg:rotate-0   transition-all duration-300 ease-in-out`}
                />
                <hr className="w-[1px] h-full bg-[#F0EFFB] ml-[14px] mr-[9px]" />
                <QuestionIcon
                  onClick={() => handleAction("doubt")}
                  className={`${
                    isDoubtProperty
                      ? "fill-[#FC9D47]"
                      : "fill-white hover:fill-[#FEEDDA]"
                  } cursor-pointer  -rotate-90  lg:rotate-0   transition-all duration-300 ease-in-out`}
                />
                <hr className="w-[1px] h-full bg-[#F0EFFB] mr-[15.83px] ml-[11px]" />
                <RemoveIcon
                  onClick={() => handleAction("discard")}
                  className={`${
                    isDiscardedProperty ? "fill-[#FF6D59]" : "fill-white"
                  }  cursor-pointer  -rotate-90  lg:rotate-0 hover:fill-[#FFE2DE] hover: transition-all duration-300 ease-in-out`}
                />
              </div>
            </div>
            <div className="lg:mt-[12px]">
              <div className="relative flex flex-col group justify-start">
                <TooltipCursor
                  content={property?.title}
                  delay={200}
                  visible={isTruncated}
                >
                  <div>
                    <h1
                      ref={textRef}
                      className="text-[16px] md:text-[20px] lg:text-[24px] font-bold text-black leading-[1.4] md:leading-[28px] lg:leading-[36px] -tracking-[0.24px] line-clamp-1"
                    >
                      {property?.title}
                    </h1>
                  </div>
                </TooltipCursor>
              </div>

              <div className="mt-2 flex items-center justify-between">
                <p
                  ref={locationTextRef}
                  className="opacity-50  text-[16px] font-medium text-[#000929] leading-[24px]"
                >
                  {property && formatPropertyLocation(property)}
                </p>
                {isAddedBy && (
                  <p className="text-[12px] font-bold text-primary capitalize leading-[27px]">
                    Aggiunta da Anna Mr
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="pl-4 pr-3 lg:px-[29.88px] flex-grow flex flex-col">
          <hr className="h-[1px] bg-[#F0EFFB] w-full mt-4" />
          <div className="flex items-center justify-between xs:justify-normal xs:space-x-[24px] flex-grow">
            <div className="flex items-center space-x-2">
              <RoomIcon className="text-primary w-[23px] h-[23px]" />
              <p className="text-[14px] font-normal text-[#6C727F] leading-[19.6px]">
                {property?.rooms || 0}
              </p>
            </div>
            <div className="flex items-center space-x-2">
              <BedroomIcon className="stroke-primary text-primary" />
              <p className="text-[14px] font-normal text-[#6C727F] leading-[19.6px]">
                {property?.bedrooms || 0}
              </p>
            </div>
            <div className="flex items-center space-x-2">
              <BathIcon />
              <p className="text-[14px] font-normal text-[#6C727F] leading-[19.6px]">
                {property?.bathrooms || 0}
              </p>
            </div>
            <div className="flex items-center space-x-2">
              <AreaIcon />
              <p className="text-[14px] font-normal text-[#6C727F] flex-shrink-0 leading-[19.6px]">
                {property?.total_area || 0} m²
              </p>
            </div>
          </div>

          {/* Mobile location button  */}
          <div
            onClick={() =>
              navigation(
                `/property/${property?._id}?average_gross_yield=${property?.average_gross_yield}`
              )
            }
            className="w-full md:hidden mb-4 inline-flex bg-primary mt-[15px] items-center justify-center space-x-[10px]  rounded-[8px] h-[38px] hover:opacity-90 duration-300 ease-in-out"
          >
            <LocationIcon className="text-white" />
            <p className="text-white font-semibold text-[12px]">
              Apri Annuncio
            </p>
          </div>
        </div>
        <RemoveWarningDialog
          warningText={removeWarningPopupOpen}
          isOpen={removeWarningPopupOpen}
          isClose={() => setRemoveWarningPopupOpen(null)}
          onClick={handleRemoveConfirm}
          buttonText2="Annulla"
        />
      </div>
      <PropertyNoteDialog
        id={property?._id}
        onClick={handleUpdateNote}
        loading={loading}
        defaultText={isBookmarkPropertyHasNote || ""}
      />
      <ChooseAndCreateOrganiseList
        isOpen={chooseOrganiseList}
        onClose={() => setChooseOrganiseList(false)}
        onSaveClick={handleAddBookmark}
      />
    </>
  );
};

export default PropertyCard;
